import { z, ZodError } from "zod";

// We've defined here the validations & schema for making sure the env vars ARE correct.
const defaultValidation = z.string().min(1, "Env Var is not defined");
const envSchema = z.object({
  VITE_APP_ENV: defaultValidation,
  VITE_APP_URL: defaultValidation,
  VITE_GOOGLE_AUTH_SSO_CLIENT_ID: defaultValidation,
  VITE_API_URL: defaultValidation,
  VITE_SENTRY_DSN: defaultValidation,
  VITE_SENTRY_TRACE_PROPAGATION_TARGET_REGEX: defaultValidation,
  VITE_PAGINATION_DEFAULT_SIZE: z.string().regex(/^\d+$/).transform(Number),
  VITE_INTAKE_FORM_ID: z.coerce.number(),
  VITE_EMAIL_THROTTLE: z.coerce.number(),
  VITE_MIGHTY_PROFILE_URL: defaultValidation,
  VITE_AWS_RUM_APPLICATION_ID: defaultValidation,
  VITE_AWS_RUM_APPLICATION_VERSION: defaultValidation,
  VITE_AWS_RUM_APPLICATION_REGION: defaultValidation,
  VITE_AWS_RUM_GUEST_ROLE_ARN: defaultValidation,
  VITE_AWS_RUM_IDENTITY_POOL_ID: defaultValidation,
  VITE_AWS_RUM_ENDPOINT: defaultValidation,
  VITE_OURS_CONSENT_TOKEN: z.string().optional(),
  VITE_SUPPORT_PHONE_NUMBER: z.string().default("(646) 760-6669"),
  VITE_SUPPORT_PHONE_NUMBER_RAW: z.string().default("+16467606669"),
  VITE_ELEKTRA_CARE_EMAIL: z.string().default("care@elektrahealth.com"),
  VITE_INITIAL_VISIT_COST: z.coerce.number().default(249),
  VITE_FOLLOWUP_VISIT_COST: z.coerce.number().default(149),
  VITE_COACH_VISIT_COST: z.coerce.number().default(99),
  VITE_LATE_CANCELLATION_FEE: z.coerce.number().default(50),
  VITE_AB_TEST_PROVIDER_VIEW_PERCENTAGE: z.coerce.number().default(0.2),
  VITE_AB_TEST_QUESTION_AND_CONTEXT_PERCENTAGE: z.coerce.number().default(0.4),
  VITE_AB_TEST_ONLY_CONTEXT_PERCENTAGE: z.coerce.number().default(0.4),
  VITE_AB_TEST_NIRVANA_PERCENTAGE: z.coerce.number().default(0.5),
  VITE_APPOINTMENT_SLOT_MAX_MONTHS: z.coerce.number().default(4),
  VITE_APPOINTMENT_VISITS_COOLDOWN: z.coerce.number().default(15000),
  VITE_INSURANCE_REQUIRED_HOURS_THRESHOLD: z
    .string()
    .regex(/^\d+$/)
    .transform(Number)
    .default(72),
});

type EnvValues = z.infer<typeof envSchema>;

// IDEA: trigger a toast message on dev instead of just a console error
function logEnvError(errors: ZodError<EnvValues>) {
  const { ...formattedErrors } = errors.format();

  console.error("<");
  console.error("ENVIRONMENT VARIABLES ERRORS:");
  console.error("----");
  Object.entries(formattedErrors).forEach(([name]) => {
    console.error(name);
  });
  console.error("----");
  console.error(">");
}

function checkEnv() {
  try {
    return envSchema.parse(import.meta.env);
  } catch (errors) {
    if (errors instanceof ZodError) {
      logEnvError(errors as ZodError<EnvValues>);
    }

    // So! We had some validation errors
    // let's just make sure the shape of the object is what we are expecting
    return Object.fromEntries(
      Object.keys(envSchema.shape).map((key) => [
        key,
        import.meta.env[key] || "",
      ]),
    ) as EnvValues;
  }
}

export const env = checkEnv();
